"use client";

import React, { useState, useRef, Suspense } from "react";
import { useQueryParam } from "@/lib/routing";
import { Input } from "@/app/components/ui/input";
import { Button } from "@/app/components/ui/button";
import { Slider } from "@/app/components/ui/slider";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/app/components/ui/card";
import { Label } from "@/app/components/ui/label";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/app/components/ui/tabs";
import FileUpload from "@/app/components/FileUpload";
import MultiFileUpload from "@/app/components/MultiFileUpload";
import { useAuth } from "@/hooks/useAuth";
import { ImageIcon, VideoIcon, RadioIcon, FileIcon } from "lucide-react";
import { toast } from "react-hot-toast";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/app/components/ui/select";
import { CardFooter } from "@/app/components/ui/card";
import { Loader2, Download } from "lucide-react";
import { mediaAPI } from "@/lib/media-api";

// Helper function to check if URL is valid
const isValidUrl = (value: string) => {
  try {
    new URL(value);
    return true;
  } catch {
    return false;
  }
};

const COMPRESS_TABS = ["image", "video", "audio", "file"] as const;
type CompressTabValue = typeof COMPRESS_TABS[number];

function CompressPageContent() {
  const { user } = useAuth();
  
  // Single-param state for `tab` with type safety
  const [activeTab, setActiveTab] = useQueryParam<CompressTabValue>("tab", {
    parse: (raw) => (raw ?? "image") as CompressTabValue,
    serialize: (v) => v ?? null,
    defaultValue: "image",
    replace: true, // keep history clean while switching tabs
  });
  
  return (
    <div className="flex min-h-screen items-center justify-center p-4 animate-fade-in">
      <div className="container mx-auto flex justify-center">
        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as CompressTabValue)} className="w-full max-w-2xl">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="image">
              <ImageIcon className="mr-2 h-4 w-4" /> Images
            </TabsTrigger>
            <TabsTrigger value="video">
              <VideoIcon className="mr-2 h-4 w-4" /> Videos
            </TabsTrigger>
            <TabsTrigger value="audio">
              <RadioIcon className="mr-2 h-4 w-4" /> Audio
            </TabsTrigger>
            <TabsTrigger value="file">
              <FileIcon className="mr-2 h-4 w-4" /> Other Files
            </TabsTrigger>
          </TabsList>

          {/* IMAGE COMPRESSION TAB */}
          <TabsContent value="image">
            <ImageCompressor />
          </TabsContent>

          {/* VIDEO COMPRESSION TAB */}
          <TabsContent value="video">
            <VideoCompressor />
          </TabsContent>

          {/* AUDIO COMPRESSION TAB */}
          <TabsContent value="audio">
            <AudioCompressor />
          </TabsContent>

          {/* FILE COMPRESSION TAB */}
          <TabsContent value="file">
            <FileCompressor />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

// Image Compression Component
function ImageCompressor() {
  const { user } = useAuth();
  const [file, setFile] = useState<File | null>(null);
  const [quality, setQuality] = useState(80);
  const [format, setFormat] = useState("jpg");
  const [isCompressing, setIsCompressing] = useState(false);
  const [compressedUrl, setCompressedUrl] = useState<string | null>(null);
  const [compressedFilename, setCompressedFilename] = useState<string | null>(
    null
  );

  // Image compression will use mediaAPI instead of TRPC

  const handleFileChange = (selectedFile: File | null) => {
    setFile(selectedFile);
    setCompressedUrl(null);
    setCompressedFilename(null);
  };

  const handleCompress = async () => {
    if (!file) return;

    setIsCompressing(true);
    setCompressedUrl(null);
    setCompressedFilename(null);

    try {
      // Upload file first to get file path
      const formData = new FormData();
      formData.append("file", file);
      
      const uploadResponse = await fetch("/api/upload", {
        method: "POST",
        body: formData,
      });
      
      if (!uploadResponse.ok) {
        throw new Error("File upload failed");
      }
      
      const uploadResult = await uploadResponse.json();
      const { bucket, key } = uploadResult;

      // Use mediaAPI to compress the image
      const result = await mediaAPI.compressImage({
        bucket,
        key,
        format: format as any,
        quality
      });

      // Use the URL directly from the result
      const url = result.url;

      setCompressedUrl(url);
      setCompressedFilename(result.filename);
      toast.success("Image compressed successfully!");

      // Log to history
      if (user) {
        fetch('/api/activity/log', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            activityType: 'image_compressed',
            toolName: 'compress-image-ui',
            metadata: { format, quality, filename: result.filename },
          }),
        }).catch(() => {});
      }
    } catch (error: any) {
      console.error(error);
      toast.error(error.message || "Failed to compress image");
    } finally {
      setIsCompressing(false);
    }
  };

  const handleDownload = () => {
    if (compressedUrl && compressedFilename) {
      const a = document.createElement("a");
      a.href = compressedUrl;
      a.download = compressedFilename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  return (
    <Card className="w-full card-enhanced">
      <CardHeader>
        <CardTitle>Image Compressor</CardTitle>
        <CardDescription>
          Compress and optimize your images with custom settings.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <FileUpload
          id="image-file"
          label="Select Image"
          accept="image/*"
          onFileChangeAction={handleFileChange}
          disabled={isCompressing}
          showPreview={true}
          file={file}
          maxSize={50}
          placeholder="Choose an image file or drag it here"
        />

        <div className="space-y-2">
          <Label htmlFor="format">Output Format</Label>
          <Select
            defaultValue={format}
            onValueChange={setFormat}
            disabled={isCompressing || !file}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select format" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="jpg">JPEG</SelectItem>
              <SelectItem value="png">PNG</SelectItem>
              <SelectItem value="webp">WebP</SelectItem>
              <SelectItem value="avif">AVIF</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <div className="flex justify-between">
            <Label htmlFor="quality">Quality: {quality}%</Label>
          </div>
          <Slider
            id="quality"
            min={1}
            max={100}
            step={1}
            value={[quality]}
            onValueChange={(value) => setQuality(value[0])}
            disabled={isCompressing || !file}
            className="py-2"
          />
        </div>

        {compressedUrl && (
          <div className="space-y-2 border-t pt-4">
            <Label>Compressed Image</Label>
            <div className="border rounded-md overflow-hidden">
              <img
                src={compressedUrl}
                alt="Compressed"
                className="max-h-48 w-auto mx-auto object-contain"
              />
            </div>
            <Button
              variant="outline"
              className="w-full hover-lift"
              onClick={handleDownload}
            >
              <Download className="mr-2 h-4 w-4" />
              Download Compressed Image
            </Button>
          </div>
        )}
      </CardContent>
      <CardFooter>
        <Button
          className="w-full btn-enhanced hover-lift"
          onClick={handleCompress}
          disabled={!file || isCompressing}
        >
          {isCompressing ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Compressing...
            </>
          ) : (
            "Compress Image"
          )}
        </Button>
      </CardFooter>
    </Card>
  );
}

// Video Compression Component
function VideoCompressor() {
  const { user } = useAuth();
  const [file, setFile] = useState<File | null>(null);
  const [bitrate, setBitrate] = useState(1000); // kbps
  const [resolution, setResolution] = useState("720p");
  const [format, setFormat] = useState("mp4");
  const [isCompressing, setIsCompressing] = useState(false);
  const [compressedUrl, setCompressedUrl] = useState<string | null>(null);
  const [compressedFilename, setCompressedFilename] = useState<string | null>(
    null
  );

  // Video compression will use mediaAPI instead of TRPC

  const handleFileChange = (selectedFile: File | null) => {
    setFile(selectedFile);
    setCompressedUrl(null);
    setCompressedFilename(null);
  };

  const handleCompress = async () => {
    if (!file) return;

    setIsCompressing(true);
    setCompressedUrl(null);
    setCompressedFilename(null);

    try {
      // Upload file first to get file path
      const formData = new FormData();
      formData.append("file", file);
      
      const uploadResponse = await fetch("/api/upload", {
        method: "POST",
        body: formData,
      });
      
      if (!uploadResponse.ok) {
        throw new Error("File upload failed");
      }
      
      const uploadResult = await uploadResponse.json();
      const { bucket, key } = uploadResult;

      // Use mediaAPI to compress the video
      const result = await mediaAPI.compressVideo({
        bucket,
        key,
        format: format as any,
        bitrate,
        resolution: "720p" // Default resolution
      });

      // Use the URL directly from the result
      const url = result.url;

      setCompressedUrl(url);
      setCompressedFilename(result.filename);
      toast.success("Video compressed successfully!");

      // Log to history
      if (user) {
        fetch('/api/activity/log', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            activityType: 'video_compressed',
            toolName: 'compress-video-ui',
            metadata: { format, bitrate, resolution, filename: result.filename },
          }),
        }).catch(() => {});
      }
    } catch (error: any) {
      console.error(error);
      toast.error(error.message || "Failed to compress video");
    } finally {
      setIsCompressing(false);
    }
  };

  const handleDownload = () => {
    if (compressedUrl && compressedFilename) {
      const a = document.createElement("a");
      a.href = compressedUrl;
      a.download = compressedFilename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  return (
    <Card className="w-full card-enhanced">
      <CardHeader>
        <CardTitle>Video Compressor</CardTitle>
        <CardDescription>
          Compress videos with custom quality and resolution settings.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <FileUpload
          id="video-file"
          label="Select Video"
          accept="video/*"
          onFileChangeAction={handleFileChange}
          disabled={isCompressing}
          file={file}
          maxSize={500}
          placeholder="Choose a video file or drag it here"
        />

        <div className="space-y-2">
          <Label htmlFor="format">Output Format</Label>
          <Select
            defaultValue={format}
            onValueChange={setFormat}
            disabled={isCompressing || !file}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select format" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="mp4">MP4</SelectItem>
              <SelectItem value="webm">WebM</SelectItem>
              <SelectItem value="mov">MOV</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="resolution">Resolution</Label>
          <Select
            defaultValue={resolution}
            onValueChange={setResolution}
            disabled={isCompressing || !file}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select resolution" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="480p">480p</SelectItem>
              <SelectItem value="720p">720p</SelectItem>
              <SelectItem value="1080p">1080p</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <div className="flex justify-between">
            <Label htmlFor="bitrate">Bitrate: {bitrate} kbps</Label>
          </div>
          <Slider
            id="bitrate"
            min={100}
            max={5000}
            step={100}
            value={[bitrate]}
            onValueChange={(value) => setBitrate(value[0])}
            disabled={isCompressing || !file}
            className="py-2"
          />
        </div>

        {compressedUrl && (
          <div className="space-y-2 border-t pt-4">
            <Label>Compressed Video</Label>
            <Button
              variant="outline"
              className="w-full hover-lift"
              onClick={handleDownload}
            >
              <Download className="mr-2 h-4 w-4" />
              Download Compressed Video
            </Button>
          </div>
        )}
      </CardContent>
      <CardFooter>
        <Button
          className="w-full btn-enhanced hover-lift"
          onClick={handleCompress}
          disabled={!file || isCompressing}
        >
          {isCompressing ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Compressing...
            </>
          ) : (
            "Compress Video"
          )}
        </Button>
      </CardFooter>
    </Card>
  );
}

// Audio Compression Component
function AudioCompressor() {
  const { user } = useAuth();
  const [file, setFile] = useState<File | null>(null);
  const [bitrate, setBitrate] = useState(128); // kbps
  const [format, setFormat] = useState("mp3");
  const [isCompressing, setIsCompressing] = useState(false);
  const [compressedUrl, setCompressedUrl] = useState<string | null>(null);
  const [compressedFilename, setCompressedFilename] = useState<string | null>(
    null
  );

  // Audio compression will use mediaAPI instead of TRPC

  const handleFileChange = (selectedFile: File | null) => {
    setFile(selectedFile);
    setCompressedUrl(null);
    setCompressedFilename(null);
  };

  const handleCompress = async () => {
    if (!file) return;

    setIsCompressing(true);
    setCompressedUrl(null);
    setCompressedFilename(null);

    try {
      // Upload file first to get file path
      const formData = new FormData();
      formData.append("file", file);
      
      const uploadResponse = await fetch("/api/upload", {
        method: "POST",
        body: formData,
      });
      
      if (!uploadResponse.ok) {
        throw new Error("File upload failed");
      }
      
      const uploadResult = await uploadResponse.json();
      const { bucket, key } = uploadResult;

      // Use mediaAPI to compress the audio
      const result = await mediaAPI.compressAudio({
        bucket,
        key,
        format: format as any,
        bitrate
      });

      // Use the URL directly from the result
      const url = result.url;

      setCompressedUrl(url);
      setCompressedFilename(result.filename);
      toast.success("Audio compressed successfully!");

      // Log to history
      if (user) {
        fetch('/api/activity/log', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            activityType: 'audio_compressed',
            toolName: 'compress-audio-ui',
            metadata: { format, bitrate, filename: result.filename },
          }),
        }).catch(() => {});
      }
    } catch (error: any) {
      console.error(error);
      toast.error(error.message || "Failed to compress audio");
    } finally {
      setIsCompressing(false);
    }
  };

  const handleDownload = () => {
    if (compressedUrl && compressedFilename) {
      const a = document.createElement("a");
      a.href = compressedUrl;
      a.download = compressedFilename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  return (
    <Card className="w-full card-enhanced">
      <CardHeader>
        <CardTitle>Audio Compressor</CardTitle>
        <CardDescription>
          Compress audio files with custom bitrate and format settings.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <FileUpload
          id="audio-file"
          label="Select Audio"
          accept="audio/*"
          onFileChangeAction={handleFileChange}
          disabled={isCompressing}
          file={file}
          maxSize={100}
          placeholder="Choose an audio file or drag it here"
        />

        <div className="space-y-2">
          <Label htmlFor="format">Output Format</Label>
          <Select
            defaultValue={format}
            onValueChange={setFormat}
            disabled={isCompressing || !file}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select format" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="mp3">MP3</SelectItem>
              <SelectItem value="ogg">OGG</SelectItem>
              <SelectItem value="aac">AAC</SelectItem>
              <SelectItem value="wav">WAV</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <div className="flex justify-between">
            <Label htmlFor="bitrate">Bitrate: {bitrate} kbps</Label>
          </div>
          <Slider
            id="bitrate"
            min={32}
            max={320}
            step={8}
            value={[bitrate]}
            onValueChange={(value) => setBitrate(value[0])}
            disabled={isCompressing || !file}
            className="py-2"
          />
        </div>

        {compressedUrl && (
          <div className="space-y-2 border-t pt-4">
            <Label>Compressed Audio</Label>
            <audio controls className="w-full">
              <source src={compressedUrl} type={`audio/${format}`} />
              Your browser does not support the audio element.
            </audio>
            <Button
              variant="outline"
              className="w-full hover-lift"
              onClick={handleDownload}
            >
              <Download className="mr-2 h-4 w-4" />
              Download Compressed Audio
            </Button>
          </div>
        )}
      </CardContent>
      <CardFooter>
        <Button
          className="w-full btn-enhanced hover-lift"
          onClick={handleCompress}
          disabled={!file || isCompressing}
        >
          {isCompressing ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Compressing...
            </>
          ) : (
            "Compress Audio"
          )}
        </Button>
      </CardFooter>
    </Card>
  );
}

// Generic File Compression Component
function FileCompressor() {
  const { user } = useAuth();
  const [files, setFiles] = useState<FileList | null>(null);
  const [compressionLevel, setCompressionLevel] = useState(6);
  const [format, setFormat] = useState("zip");
  const [isCompressing, setIsCompressing] = useState(false);
  const [compressedUrl, setCompressedUrl] = useState<string | null>(null);
  const [compressedFilename, setCompressedFilename] = useState<string | null>(
    null
  );

  const handleFileChange = (selectedFiles: FileList | null) => {
    setFiles(selectedFiles);
    setCompressedUrl(null);
    setCompressedFilename(null);
  };

  const handleCompress = async () => {
    if (!files || files.length === 0) return;

    setIsCompressing(true);
    setCompressedUrl(null);
    setCompressedFilename(null);

    try {
      const formData = new FormData();
      for (let i = 0; i < files.length; i++) {
        formData.append("files", files[i]);
      }
      formData.append("format", format);
      formData.append("level", compressionLevel.toString());

      const response = await fetch("/api/compress/file", {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to compress files");
      }

      // Get the filename from Content-Disposition header
      const disposition = response.headers.get("Content-Disposition");
      const filenameMatch = disposition?.match(/filename="(.+)"/);
      const filename = filenameMatch
        ? filenameMatch[1]
        : `compressed.${format}`;

      // Create a blob URL for the compressed file
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);

      setCompressedUrl(url);
      setCompressedFilename(filename);
      toast.success("Files compressed successfully!");

      // Log to history
      if (user) {
        fetch('/api/activity/log', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            activityType: 'files_compressed',
            toolName: 'compress-file-ui',
            metadata: { format, level: compressionLevel, count: files.length },
          }),
        }).catch(() => {});
      }
    } catch (error: any) {
      console.error(error);
      toast.error(error.message || "Failed to compress files");
    } finally {
      setIsCompressing(false);
    }
  };

  const handleDownload = () => {
    if (compressedUrl && compressedFilename) {
      const a = document.createElement("a");
      a.href = compressedUrl;
      a.download = compressedFilename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  return (
    <Card className="w-full card-enhanced">
      <CardHeader>
        <CardTitle>File Compressor</CardTitle>
        <CardDescription>
          Compress any files into an archive with custom compression settings.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <MultiFileUpload
          id="files"
          label="Select Files"
          onFilesChangeAction={handleFileChange}
          disabled={isCompressing}
          files={files}
          maxSize={100}
          maxFiles={20}
          placeholder="Choose files to compress or drag them here"
        />

        <div className="space-y-2">
          <Label htmlFor="format">Archive Format</Label>
          <Select
            defaultValue={format}
            onValueChange={setFormat}
            disabled={isCompressing || !files}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select format" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="zip">ZIP</SelectItem>
              <SelectItem value="tar">TAR</SelectItem>
              <SelectItem value="gz">GZ</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <div className="flex justify-between">
            <Label htmlFor="level">
              Compression Level: {compressionLevel} (1-9)
            </Label>
          </div>
          <Slider
            id="level"
            min={1}
            max={9}
            step={1}
            value={[compressionLevel]}
            onValueChange={(value) => setCompressionLevel(value[0])}
            disabled={isCompressing || !files}
            className="py-2"
          />
        </div>

        {compressedUrl && (
          <div className="space-y-2 border-t pt-4">
            <Label>Compressed Archive</Label>
            <Button
              variant="outline"
              className="w-full hover-lift"
              onClick={handleDownload}
            >
              <Download className="mr-2 h-4 w-4" />
              Download Compressed Archive
            </Button>
          </div>
        )}
      </CardContent>
      <CardFooter>
        <Button
          className="w-full btn-enhanced hover-lift"
          onClick={handleCompress}
          disabled={!files || files.length === 0 || isCompressing}
        >
          {isCompressing ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Compressing...
            </>
          ) : (
            "Compress Files"
          )}
        </Button>
      </CardFooter>
    </Card>
  );
}

export default function CompressPage() {
  return (
    <Suspense fallback={<div className="container mx-auto px-4 py-8">Loading...</div>}>
      <CompressPageContent />
    </Suspense>
  );
}
